import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Button from '../components/Button';
import Input from '../components/Input';
import { adminManagementApi } from '../services/api';
import { cardStyles, colors, utilityStyles } from '../styles/sharedStyles';

const AdminProfile = () => {
  // Profile section
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
  });
  const [profileErrors, setProfileErrors] = useState({});
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');

  // Password section
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setProfileLoading(true);
      const response = await adminManagementApi.getProfile();
      setProfile(response.data);
      setProfileForm({
        name: response.data.name,
        email: response.data.email,
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
      setProfileErrors({ fetch: 'Failed to load profile' });
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle profile form change
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm({ ...profileForm, [name]: value });
    if (profileErrors[name]) {
      setProfileErrors({ ...profileErrors, [name]: '' });
    }
    setProfileSuccess('');
  };

  // Validate profile form
  const validateProfileForm = () => {
    const errors = {};
    if (!profileForm.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!profileForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(profileForm.email)) {
      errors.email = 'Invalid email format';
    }
    return errors;
  };

  // Submit profile update
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const errors = validateProfileForm();
    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    try {
      setProfileSaving(true);
      setProfileErrors({});
      setProfileSuccess('');
      await adminManagementApi.updateProfile(profileForm);
      setProfileSuccess('Profile updated successfully!');
      // Update localStorage
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      adminUser.name = profileForm.name;
      adminUser.email = profileForm.email;
      localStorage.setItem('adminUser', JSON.stringify(adminUser));
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileErrors({
        submit: err.response?.data?.message || 'Failed to update profile',
      });
    } finally {
      setProfileSaving(false);
    }
  };

  // Handle password form change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm({ ...passwordForm, [name]: value });
    if (passwordErrors[name]) {
      setPasswordErrors({ ...passwordErrors, [name]: '' });
    }
    setPasswordSuccess('');
  };

  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }
    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    return errors;
  };

  // Submit password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const errors = validatePasswordForm();
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    try {
      setPasswordSaving(true);
      setPasswordErrors({});
      setPasswordSuccess('');
      await adminManagementApi.changePassword(passwordForm);
      setPasswordSuccess('Password changed successfully!');
      // Clear form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordErrors({
        submit: err.response?.data?.message || 'Failed to change password',
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  if (profileLoading) {
    return (
      <Layout title="My Profile">
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading profile...</div>
      </Layout>
    );
  }

  return (
    <Layout title="My Profile">
      <div style={{ maxWidth: '800px' }}>
        {/* Profile Information Card */}
        <div style={cardStyles.base}>
          <h3 style={cardStyles.header}>Profile Information</h3>
          <form onSubmit={handleProfileSubmit}>
            <Input
              label="Name"
              name="name"
              value={profileForm.name}
              onChange={handleProfileChange}
              error={profileErrors.name}
              required
            />
            <Input
              label="Email"
              name="email"
              type="email"
              value={profileForm.email}
              onChange={handleProfileChange}
              error={profileErrors.email}
              required
            />
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: colors.textSecondary }}>
                Role
              </label>
              <div style={{ fontSize: '14px', color: colors.textPrimary }}>
                {profile?.role || 'N/A'}
              </div>
            </div>
            {profileErrors.submit && (
              <div
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  borderRadius: '4px',
                  marginBottom: '16px',
                }}
              >
                {profileErrors.submit}
              </div>
            )}
            {profileSuccess && (
              <div
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#d4edda',
                  color: '#155724',
                  borderRadius: '4px',
                  marginBottom: '16px',
                }}
              >
                {profileSuccess}
              </div>
            )}
            <Button type="submit" disabled={profileSaving}>
              {profileSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </div>

        {/* Change Password Card */}
        <div style={cardStyles.base}>
          <h3 style={cardStyles.header}>Change Password</h3>
          <form onSubmit={handlePasswordSubmit}>
            <Input
              label="Current Password"
              name="currentPassword"
              type="password"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              error={passwordErrors.currentPassword}
              required
            />
            <Input
              label="New Password"
              name="newPassword"
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              error={passwordErrors.newPassword}
              required
            />
            <Input
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              error={passwordErrors.confirmPassword}
              required
            />
            {passwordErrors.submit && (
              <div
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  borderRadius: '4px',
                  marginBottom: '16px',
                }}
              >
                {passwordErrors.submit}
              </div>
            )}
            {passwordSuccess && (
              <div
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#d4edda',
                  color: '#155724',
                  borderRadius: '4px',
                  marginBottom: '16px',
                }}
              >
                {passwordSuccess}
              </div>
            )}
            <Button type="submit" disabled={passwordSaving}>
              {passwordSaving ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AdminProfile;
